/**
 * Wave CF-P — platform admin view of the shared court-form library: upload a
 * new form or a newer edition, correct details, mark verified, retire / restore.
 * Whatever is added here is the one copy every firm draws from, limited by the
 * jurisdiction blocks each firm's plan includes.
 * Declared at module scope (standing React rule).
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Archive, DeleteOutline, OpenInNew, Unarchive, UploadFile, Verified } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';

const BASE = '/api/admin/super/court-forms';

type Ref = { key: string; label: string };
type AdminForm = {
  id: string; issuer: string; code: string; title: string; kind: string; jurisdiction: string; practiceArea: string;
  editionDate: string | null; lastVerifiedAt: string | null; fillable: string; pageCount: number; fileSize: number;
  isActive: boolean; supersededBy: string | null; createdAt: string;
};

async function call(url: string, init?: RequestInit, fallback = 'Request failed') {
  const isForm = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${getAuthToken()}`, ...(init?.body && !isForm ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  return res.json();
}

async function openFile(id: string) {
  try {
    const res = await fetch(`${BASE}/${id}/file`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to open form'));
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    toast.error(toUserErrorMessage(err, 'Unable to open form'));
  }
}

const day = (d: string | null) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : '—');
const blank = { issuer: '', code: '', title: '', jurisdiction: '', practiceArea: '', kind: 'form', editionDate: '', sourceUrl: '', allowOlder: false };

interface UploadProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  issuers: string[];
  jurisdictions: Ref[];
  practiceAreas: Ref[];
  existing: AdminForm[];
}

const UploadFormDialog: React.FC<UploadProps> = ({ open, onClose, onDone, issuers, jurisdictions, practiceAreas, existing }) => {
  const [f, setF] = useState({ ...blank });
  const [file, setFile] = useState<File | null>(null);
  const set = (patch: Partial<typeof blank>) => setF((prev) => ({ ...prev, ...patch }));

  const match = useMemo(
    () => existing.find((e) => e.isActive && e.kind === f.kind && e.issuer === f.issuer.trim().toUpperCase() && e.code.toUpperCase() === f.code.trim().toUpperCase()),
    [existing, f.issuer, f.code, f.kind]
  );

  const close = () => { setF({ ...blank }); setFile(null); onClose(); };

  const save = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file as File);
      fd.append('issuer', f.issuer.trim().toUpperCase());
      fd.append('code', f.code.trim());
      fd.append('title', f.title.trim());
      fd.append('jurisdiction', f.jurisdiction);
      fd.append('practiceArea', f.practiceArea);
      fd.append('kind', f.kind);
      if (f.editionDate) fd.append('editionDate', f.editionDate);
      if (f.sourceUrl.trim()) fd.append('sourceUrl', f.sourceUrl.trim());
      if (f.allowOlder) fd.append('allowOlder', 'true');
      return call(BASE, { method: 'POST', body: fd }, 'Unable to upload the form');
    },
    onSuccess: (r) => {
      const o = r.data.outcome;
      toast.success(
        o === 'created' ? `${r.data.form.code} added to the library`
          : o === 'newEdition' ? `${r.data.form.code}: new edition is now current; the previous edition was kept`
            : `${r.data.form.code}: same file as the current edition — details refreshed`
      );
      if (r.data.stillLocked) toast('Saved, but the agency edit lock could not be removed on this server — the form opens and prints normally.', { icon: '⚠️', duration: 8000 });
      onDone();
      close();
    },
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to upload the form')),
  });

  const ready = Boolean(file && f.issuer.trim().length >= 2 && f.code.trim() && f.title.trim().length >= 3 && f.jurisdiction && f.practiceArea);

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle>Add a form to the platform library</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            The form is stored once and becomes available to every firm whose plan includes its jurisdiction. Firms still choose which forms they use.
          </Alert>
          <Box>
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              {file ? 'Choose a different PDF' : 'Choose PDF'}
              <input hidden type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            {file && (
              <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              PDF only, up to 20 MB. Download it from the agency or court website so the edition is current.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={issuers}
                inputValue={f.issuer}
                onInputChange={(_, v) => set({ issuer: v.toUpperCase() })}
                renderInput={(params) => <TextField {...params} required label="Issuer" helperText="e.g. USCIS, EOIR, CA-JC, TX-OCA" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth required label="Form number" value={f.code} onChange={(e) => set({ code: e.target.value })} helperText="e.g. I-130, FL-100" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="pl-kind">Type</InputLabel>
                <Select labelId="pl-kind" label="Type" value={f.kind} onChange={(e) => set({ kind: e.target.value })}>
                  <MenuItem value="form">Form</MenuItem>
                  <MenuItem value="instructions">Instructions</MenuItem>
                  <MenuItem value="info">Information sheet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Title" value={f.title} onChange={(e) => set({ title: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="pl-j">Jurisdiction</InputLabel>
                <Select labelId="pl-j" label="Jurisdiction" value={f.jurisdiction} onChange={(e) => set({ jurisdiction: e.target.value })} MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}>
                  {jurisdictions.map((j) => <MenuItem key={j.key} value={j.key}>{j.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="pl-a">Practice area</InputLabel>
                <Select labelId="pl-a" label="Practice area" value={f.practiceArea} onChange={(e) => set({ practiceArea: e.target.value })}>
                  {practiceAreas.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Edition date" InputLabelProps={{ shrink: true }} value={f.editionDate} onChange={(e) => set({ editionDate: e.target.value })} helperText="Printed on the form" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Source link (optional)" placeholder="https://" value={f.sourceUrl} onChange={(e) => set({ sourceUrl: e.target.value })} />
            </Grid>
          </Grid>
          {match && (
            <Alert severity="warning">
              {match.code} is already in the library (edition {day(match.editionDate)}). Uploading replaces it as the current edition for every firm;
              the old edition is kept and matters keep the copies they already have.
              <FormControlLabel
                sx={{ display: 'block', mt: 1 }}
                control={<Checkbox size="small" checked={f.allowOlder} onChange={(e) => set({ allowOlder: e.target.checked })} />}
                label="Replace it even if this edition date is older"
              />
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Uploading…' : match ? 'Upload new edition' : 'Add to library'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteProps {
  form: AdminForm | null;
  onClose: () => void;
  onDone: () => void;
}

const DeleteFormDialog: React.FC<DeleteProps> = ({ form, onClose, onDone }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['court-forms', 'platform-usage', form?.id],
    queryFn: () => call(`${BASE}/${form?.id}/usage`, undefined, 'Unable to check usage'),
    enabled: Boolean(form),
  });
  const usage = data?.data;
  const remove = useMutation({
    mutationFn: () => call(`${BASE}/${form?.id}`, { method: 'DELETE' }, 'Unable to delete the form'),
    onSuccess: () => { toast.success(`${form?.code} deleted from the library`); onDone(); onClose(); },
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to delete the form')),
  });
  return (
    <Dialog open={Boolean(form)} onClose={remove.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete {form?.code} from the library?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            {form?.issuer} · {form?.jurisdiction} · edition {day(form?.editionDate || null)}<br />{form?.title}
          </Typography>
          {isLoading && <CircularProgress size={24} />}
          {usage && (
            <Alert severity={usage.firmsEnabled || usage.mattersWithCopy ? 'warning' : 'info'}>
              {usage.firmsEnabled} firm(s) have this form switched on and {usage.mattersWithCopy} matter(s) hold a copy of this edition.
              {' '}Copies already on matters stay there. Firms will no longer be able to add it, and packets that use it will show it as missing.
              {usage.otherEditions > 0 && form?.isActive && ' An earlier edition exists — it is not brought back automatically; restore it from Retired / superseded if it should be current.'}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Use Delete for a wrong upload. To take a genuine form out of use, Retire it instead — a retired form can be restored.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={remove.isPending}>Cancel</Button>
        <Button color="error" variant="contained" disabled={!usage || remove.isPending} onClick={() => remove.mutate()}>
          {remove.isPending ? 'Deleting…' : 'Delete form'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PlatformLibraryTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'active' | 'retired' | 'all'>('active');
  const [juris, setJuris] = useState('');
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleting, setDeleting] = useState<AdminForm | null>(null);

  const params = new URLSearchParams({ status });
  if (juris) params.set('jurisdiction', juris);
  if (search.trim().length >= 2) params.set('search', search.trim());

  const { data, isLoading, error } = useQuery({
    queryKey: ['court-forms', 'platform', params.toString()],
    queryFn: () => call(`${BASE}?${params}`, undefined, 'Unable to load the library'),
  });
  const forms: AdminForm[] = data?.data?.forms || [];
  const jurisdictions: Ref[] = data?.data?.jurisdictions || [];
  const practiceAreas: Ref[] = data?.data?.practiceAreas || [];
  const areaLabel = useMemo(() => Object.fromEntries(practiceAreas.map((p) => [p.key, p.label])), [practiceAreas]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['court-forms'] });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'retire' | 'restore' | 'verify' }) =>
      action === 'verify'
        ? call(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify({ markVerified: true }) }, 'Unable to update')
        : call(`${BASE}/${id}/${action}`, { method: 'POST' }, 'Unable to update'),
    onSuccess: (_r, v) => {
      toast.success(v.action === 'verify' ? 'Marked as verified today' : v.action === 'retire' ? 'Form retired — hidden from every firm' : 'Form restored');
      refresh();
    },
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to update')),
  });

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Search form number or title" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="pl-fj">Jurisdiction</InputLabel>
            <Select labelId="pl-fj" label="Jurisdiction" value={juris} onChange={(e) => setJuris(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}>
              <MenuItem value="">All</MenuItem>
              {jurisdictions.map((j) => <MenuItem key={j.key} value={j.key}>{j.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="pl-st">Show</InputLabel>
            <Select labelId="pl-st" label="Show" value={status} onChange={(e) => setStatus(e.target.value as any)}>
              <MenuItem value="active">Current</MenuItem>
              <MenuItem value="retired">Retired / superseded</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3} sx={{ textAlign: { md: 'right' } }}>
          <Button variant="contained" startIcon={<UploadFile />} onClick={() => setUploadOpen(true)} disabled={!data}>Upload form</Button>
        </Grid>
      </Grid>

      {isLoading && <CircularProgress size={28} />}
      {error && <Alert severity="error">{toUserErrorMessage(error, 'Unable to load the library')}</Alert>}
      {!isLoading && !error && !forms.length && <Alert severity="info">No forms match.</Alert>}
      {forms.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">{forms.length} shown</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Form</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell>Edition</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <strong>{f.code}</strong>{f.kind !== 'form' && <Chip size="small" label={f.kind} sx={{ ml: 1 }} />}
                      <Typography variant="caption" color="text.secondary" display="block">{f.issuer} · {f.jurisdiction}</Typography>
                    </TableCell>
                    <TableCell>{f.title}</TableCell>
                    <TableCell>{areaLabel[f.practiceArea] || f.practiceArea}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(f.editionDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.lastVerifiedAt ? new Date(f.lastVerifiedAt).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      {f.isActive ? <Chip size="small" color="success" variant="outlined" label="Current" />
                        : <Chip size="small" variant="outlined" label={f.supersededBy ? 'Superseded' : 'Retired'} />}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Open PDF">
                        <IconButton size="small" aria-label={`Open ${f.code}`} onClick={() => openFile(f.id)}><OpenInNew fontSize="small" /></IconButton>
                      </Tooltip>
                      {f.isActive && (
                        <Tooltip title="I checked the agency website — this is still the current edition">
                          <IconButton size="small" aria-label={`Mark ${f.code} verified`} disabled={act.isPending} onClick={() => act.mutate({ id: f.id, action: 'verify' })}>
                            <Verified fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {f.isActive ? (
                        <Tooltip title="Retire — hide from every firm (nothing is deleted)">
                          <IconButton size="small" aria-label={`Retire ${f.code}`} disabled={act.isPending} onClick={() => act.mutate({ id: f.id, action: 'retire' })}>
                            <Archive fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Restore as the current edition">
                          <IconButton size="small" aria-label={`Restore ${f.code}`} disabled={act.isPending} onClick={() => act.mutate({ id: f.id, action: 'restore' })}>
                            <Unarchive fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete from the library (wrong upload)">
                        <IconButton size="small" color="error" aria-label={`Delete ${f.code}`} onClick={() => setDeleting(f)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <DeleteFormDialog form={deleting} onClose={() => setDeleting(null)} onDone={refresh} />
      <UploadFormDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onDone={refresh}
        issuers={data?.data?.issuers || []}
        jurisdictions={jurisdictions}
        practiceAreas={practiceAreas}
        existing={forms}
      />
    </Box>
  );
};

export default PlatformLibraryTab;
